/**
 * MultiImageUploader Component
 * Manages uploading up to `maxImages` images, showing thumbnails with remove buttons.
 *
 * Props:
 *   currentUrls  {string[]}  - existing image URLs to pre-populate (optional)
 *   folder       {string}    - Cloudinary folder: 'articles' | 'gallery' | etc.
 *   maxImages    {number}    - max allowed images (default: 10)
 *   onUpdate     {function}  - called with updated URL array whenever images change
 *   label        {string}    - section label (default: 'Images')
 */

import React, { useState, useRef, useEffect, useMemo } from 'react';
import apiClient from '../api/client';

const MultiImageUploader = ({
  currentImages = [],
  currentUrls = [],
  folder = 'gallery',
  maxImages = 10,
  onUpdate,
  onImagesChange,
  label = 'Images'
}) => {
  // Backward compatibility: use either currentImages or currentUrls
  // Use useMemo to create stable reference
  const initialImages = useMemo(() => {
    return (currentImages.length > 0 ? currentImages : currentUrls).filter(Boolean);
  }, [JSON.stringify(currentImages), JSON.stringify(currentUrls)]);
  
  // Backward compatibility: use either onImagesChange or onUpdate
  const updateCallback = onImagesChange || onUpdate;
  
  // Filter out any empty strings from pre-populated URLs
  const [images, setImages] = useState(initialImages);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  // Update images when initialImages changes
  useEffect(() => {
    setImages(initialImages);
  }, [initialImages]);

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const remaining = maxImages - images.length;
    if (remaining <= 0) {
      setError(`Maximum ${maxImages} images allowed.`);
      return;
    }

    // Only take as many files as we have slots for
    const filesToUpload = files.slice(0, remaining);
    setError('');
    setUploading(true);

    const newUrls = [];
    for (const file of filesToUpload) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('folder', folder);

        const response = await apiClient.post('/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        if (response.data.success) {
          newUrls.push(response.data.url);
        }
      } catch (err) {
        setError(err.response?.data?.error || `Failed to upload ${file.name}`);
      }
    }

    const updated = [...images, ...newUrls];
    setImages(updated);
    if (updateCallback) {
      updateCallback(updated);
    }
    setUploading(false);

    // Reset input so same files can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemove = (index) => {
    const updated = images.filter((_, i) => i !== index);
    setImages(updated);
    if (updateCallback) {
      updateCallback(updated);
    }
  };

  const canAddMore = images.length < maxImages;

  return (
    <div style={{ marginBottom: '5px' }}>
      <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
        {label} ({images.length}/{maxImages})
      </label>

      {/* Thumbnail grid */}
      {images.length > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
          gap: '8px',
          marginBottom: '12px'
        }}>
          {images.map((url, i) => (
            <div key={i} style={{ position: 'relative' }}>
              <img
                src={url}
                alt={`Upload ${i + 1}`}
                style={{
                  width: '100%',
                  height: '90px',
                  objectFit: 'cover',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  display: 'block'
                }}
                onError={(e) => { e.target.style.opacity = '0.3'; }}
              />
              <button
                type="button"
                onClick={() => handleRemove(i)}
                style={{
                  position: 'absolute',
                  top: '3px',
                  right: '3px',
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(220,53,69,0.9)',
                  color: 'white',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '12px',
                  lineHeight: '1',
                  padding: '0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Remove image"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Upload button — hidden when at max */}
      {canAddMore && (
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            multiple
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
          <button
            type="button"
            onClick={() => !uploading && fileInputRef.current?.click()}
            disabled={uploading}
            style={{
              padding: '7px 16px',
              fontSize: '13px',
              backgroundColor: uploading ? '#aaa' : '#3a2410',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: uploading ? 'not-allowed' : 'pointer'
            }}
          >
            {uploading ? 'Uploading...' : `+ Add Images (${maxImages - images.length} remaining)`}
          </button>
          <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
            JPEG, PNG, WEBP, GIF · Max 10MB each · Select multiple at once
          </small>
        </div>
      )}

      {images.length >= maxImages && (
        <p style={{ fontSize: '13px', color: '#666', margin: '4px 0 0 0' }}>
          Maximum {maxImages} images reached. Remove one to add another.
        </p>
      )}

      {error && (
        <p style={{ color: '#dc3545', fontSize: '12px', margin: '6px 0 0 0' }}>
          {error}
        </p>
      )}
    </div>
  );
};

export default MultiImageUploader;
